import { useQueuedModal } from '@/hooks/useQueuedModal';
import { Button } from '@heidi/design-system/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@heidi/design-system/dialog';
import { FramedIcon, type FramedIconElement } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyP2, TypographyPBold } from '@heidi/design-system/typography';
import { ModalId } from '@heidi/schema/ml-scribe';
import { FileText, LayoutGrid, Mic, ScanSearch } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { customTemplateOnboardingModalMessages } from './custom-template-onboarding-modal.messages';

interface ValueItemProps {
  icon: FramedIconElement;
  title: string;
  description: string;
}

function ValueItem({ icon, title, description }: ValueItemProps) {
  return (
    <div className="flex w-full items-center gap-2">
      <div className="flex shrink-0 items-start self-stretch py-1">
        <FramedIcon size="md" state="neutral" icon={icon} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TypographyPBold className="text-text-primary">{title}</TypographyPBold>
        <TypographyP2 className="text-text-secondary">{description}</TypographyP2>
      </div>
    </div>
  );
}

export function CustomTemplateOnboardingModal() {
  const { isOpen, handleClose } = useQueuedModal(ModalId.CUSTOM_TEMPLATE_ONBOARDING);
  const intl = useIntl();

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleClose}>
      <DialogDrawerContent
        className="flex h-fit flex-col items-center gap-6 border-border p-8 sm:w-[487px]"
        overlayClassName="backdrop-blur-[3px]"
      >
        <DialogHeader className="flex flex-col items-center gap-2">
          <FramedIcon size="lg" state="neutral" icon={<LayoutGrid />} />
          <DialogTitle className="w-full text-center">
            <FormattedMessage {...customTemplateOnboardingModalMessages.title} />
          </DialogTitle>
        </DialogHeader>

        <div className="flex w-full flex-col gap-4">
          <ValueItem
            icon={<ScanSearch />}
            title={intl.formatMessage(
              customTemplateOnboardingModalMessages.previewRefineSaveTitle
            )}
            description={intl.formatMessage(
              customTemplateOnboardingModalMessages.previewRefineSaveDescription
            )}
          />
          <ValueItem
            icon={<Mic />}
            title={intl.formatMessage(
              customTemplateOnboardingModalMessages.transcribeSessionTitle
            )}
            description={intl.formatMessage(
              customTemplateOnboardingModalMessages.startSessionDescription
            )}
          />
          <ValueItem
            icon={<FileText />}
            title={intl.formatMessage(
              customTemplateOnboardingModalMessages.selectCustomTemplateTitle
            )}
            description={intl.formatMessage(
              customTemplateOnboardingModalMessages.writeNotesUsingDescription
            )}
          />
        </div>

        <DialogFooter className="w-full">
          <Button className="w-full" variant="default" size="lg" onClick={handleClose}>
            <FormattedMessage {...customTemplateOnboardingModalMessages.getStarted} />
          </Button>
        </DialogFooter>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
